import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useUser } from '../../shared/context/UserContext';
import {
  getBolDetail, getProjectDetail,
  updateBolWeightAndInvoice, deleteBolDocument, getBolDocumentDownloadUrl,
} from '../../api/disposalApi';
import BolStatusBadge from '../../components/disposal/BolStatusBadge';
import BolDocumentsModal from '../../components/disposal/BolDocumentsModal';
import './BolDetailPage.css';

function fmtDate(v) { if (!v) return '—'; try { return new Date(v).toLocaleDateString('en-US'); } catch { return v; } }
function fmtDatetime(v) { if (!v) return '—'; try { return new Date(v).toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' }); } catch { return v; } }
function fmtSize(bytes) { if (!bytes) return '—'; return bytes > 1048576 ? `${(bytes/1048576).toFixed(1)} MB` : `${Math.round(bytes/1024)} KB`; }

function Chevron({ open }) {
  return (
    <span className={`bdp-card-chevron ${open ? 'open' : ''}`}>
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </span>
  );
}

export default function BolDetailPage() {
  const { projectId, bolId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { callerEmail } = useUser();
  const siteId = location.state?.site_id || '';

  const [bol, setBol] = useState(null);
  const [pricePerTon, setPricePerTon] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  // Section collapse
  const ck = (s) => `bdp_collapse_${bolId}_${s}`;
  function initC(s) { try { return localStorage.getItem(ck(s)) === 'true'; } catch { return false; } }
  const [s1, setS1] = useState(() => initC('shipment'));
  const [s2, setS2] = useState(() => initC('tools'));
  const [s3, setS3] = useState(() => initC('invoice'));
  const [s4, setS4] = useState(() => initC('docs'));
  const [s5, setS5] = useState(() => !initC('photos') ? true : false); // default collapsed
  const [s6, setS6] = useState(() => initC('sig'));
  function tog(key, val, setter) { setter(val); try { localStorage.setItem(ck(key), String(val)); } catch {} }

  // Weight & invoice edit
  const [weight, setWeight] = useState('');
  const [invoiced, setInvoiced] = useState(false);
  const [weightTicketReceived, setWeightTicketReceived] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [dirty, setDirty] = useState(false);

  // Docs modal
  const [showDocsModal, setShowDocsModal] = useState(false);

  // Lightbox
  const [lightboxUrl, setLightboxUrl] = useState(null);

  // Close the lightbox AND, if we pushed a throwaway history entry when
  // opening, pop it so the back button doesn't have a stale stack entry.
  // Guard prevents double-pop when close is already a response to popstate.
  const closeLightbox = useCallback(() => {
    setLightboxUrl(null);
    if (window.history.state && window.history.state.lightbox) {
      window.history.back();
    }
  }, []);

  // While the lightbox is open, browser back closes it (via popstate on the
  // throwaway entry pushed at open time) and Escape closes it too.
  useEffect(() => {
    if (!lightboxUrl) return;
    const onPopState = () => setLightboxUrl(null);
    const onKeyDown = (e) => { if (e.key === 'Escape') closeLightbox(); };
    window.addEventListener('popstate', onPopState);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('popstate', onPopState);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [lightboxUrl, closeLightbox]);

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(null), 3000); }

  const loadBol = useCallback(async () => {
    if (!bolId || !projectId || !callerEmail) return;
    setLoading(true); setError(null);
    try {
      const [bRes, pRes] = await Promise.all([
        getBolDetail(bolId, projectId, callerEmail),
        getProjectDetail(projectId, siteId, callerEmail).catch(() => ({ result: null })),
      ]);
      const b = bRes.result || null;
      setBol(b);
      setPricePerTon(pRes.result?.price_per_ton || null);
      if (b) {
        setWeight(b.weight_ticket_actual_lbs ?? '');
        setInvoiced(!!b.invoiced);
        setWeightTicketReceived(!!b.weight_ticket_received);
        setDirty(false);
      }
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [bolId, projectId, siteId, callerEmail]);

  useEffect(() => { loadBol(); }, [loadBol]);

  // Invoice save
  async function handleSaveInvoice() {
    setSaving(true); setSaveError(null);
    try {
      const w = weight !== '' ? parseFloat(weight) : null;
      await updateBolWeightAndInvoice(bolId, projectId, w, invoiced, callerEmail, {
        weight_ticket_received: weightTicketReceived,
      });
      showToast('BOL updated.');
      await loadBol();
    } catch (err) { setSaveError(err.message); }
    finally { setSaving(false); }
  }

  // Doc download
  async function handleDocDownload(blobName) {
    try {
      const { download_url } = await getBolDocumentDownloadUrl(blobName, callerEmail);
      window.open(download_url, '_blank');
    } catch (err) { showToast(err.message); }
  }

  // Doc delete
  async function handleDocDelete(blobName) {
    if (!window.confirm('Delete this document? This cannot be undone.')) return;
    try {
      await deleteBolDocument(bolId, projectId, blobName, callerEmail);
      showToast('Document deleted.');
      await loadBol();
    } catch (err) { showToast(err.message); }
  }

  if (loading) return <div className="bdp-page"><div className="bdp-loading"><div className="bdp-spinner" /></div></div>;
  if (error) return <div className="bdp-page"><div className="bdp-loading" style={{ color: 'var(--color-danger)' }}>{error}</div></div>;
  if (!bol) return <div className="bdp-page"><div className="bdp-loading">BOL not found</div></div>;

  const bs = (bol.status || '').toUpperCase();
  const isClosed = bs === 'CLOSED' || bs === 'AMENDED';
  const tools = bol.tools || [];
  const activeTools = tools.filter(t => !t.removed_at);
  const removedTools = tools.filter(t => t.removed_at);
  const docs = bol.bol_documents || [];
  const photos = bol.bol_photos || [];
  const hasSig = !!bol.signature_image_url;

  // Invoice calc
  const wNum = weight !== '' ? parseFloat(weight) : null;
  const invoiceAmt = (wNum && pricePerTon) ? ((wNum / 2000) * pricePerTon) : null;

  const projectNum = bol.project_number || projectId?.substring(0, 8);

  return (
    <div className="bdp-page">
      {/* Header */}
      <div className="bdp-header">
        <button className="bdp-back" onClick={() => navigate(`/admin/disposal/${projectId}`, { state: { site_id: siteId } })}>
          &larr; {projectNum}
        </button>
        <div className="bdp-header-row">
          <h1 className="bdp-bol-num">{bol.bol_number || bolId}</h1>
          <BolStatusBadge status={bol.status} />
          <span className="bdp-badge bdp-badge-neutral">{bol.oem || '—'}</span>
          <span className="bdp-badge-muted">{bol.site_name || ''}</span>
        </div>
        {bs === 'AMENDED' && bol.amended_from_bol_number && (
          <div className="bdp-amended-notice">Amended from {bol.amended_from_bol_number}</div>
        )}
      </div>

      <div className="bdp-content">
        {/* ====== SECTION 1: Shipment Details ====== */}
        <div className="bdp-card">
          <div className="bdp-card-header" onClick={() => tog('shipment', !s1, setS1)}>
            <span className="bdp-card-title">Shipment Details</span>
            <Chevron open={!s1} />
          </div>
          {!s1 && (
            <div className="bdp-card-body">
              <div className="bdp-grid">
                {[
                  ['Truck Type', bol.truck_type],
                  ['Hauler Company', bol.hauler_company],
                  ['Truck Number', bol.truck_number],
                  ['Driver Name', bol.driver_name],
                  ['Advisory Weight Limit', bol.advisory_weight_lbs != null ? `${Number(bol.advisory_weight_lbs).toLocaleString()} lbs` : null],
                  ['Created By', bol.created_by],
                  ['Created At', fmtDatetime(bol.created_at)],
                  ['Closed At', fmtDatetime(bol.closed_at)],
                ].map(([label, val]) => (
                  <div key={label} className="bdp-field">
                    <span className="bdp-field-label">{label}</span>
                    <span className={val ? 'bdp-field-value' : 'bdp-field-empty'}>{val || '—'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ====== SECTION 2: Tools on BOL ====== */}
        <div className="bdp-card">
          <div className="bdp-card-header" onClick={() => tog('tools', !s2, setS2)}>
            <span className="bdp-card-title">Tools on BOL</span>
            <Chevron open={!s2} />
          </div>
          {!s2 && (
            <div className="bdp-card-body">
              <div className="bdp-summary-line">
                {activeTools.length} active tool{activeTools.length !== 1 ? 's' : ''}
                {removedTools.length > 0 && `, ${removedTools.length} removed`}
              </div>
              {tools.length === 0 ? (
                <div className="bdp-empty">No tools have been added to this BOL yet.</div>
              ) : (
                <table className="bdp-table">
                  <thead><tr>
                    <th>Tool ID</th><th>Added At</th><th>Added By</th><th>Status</th><th>Removed Reason</th>
                  </tr></thead>
                  <tbody>
                    {tools.map((t, i) => (
                      <tr key={t.tool_id || i} className={t.removed_at ? 'bdp-row-removed' : ''}>
                        <td>{t.tool_id ? t.tool_id.substring(0, 12) : '—'}</td>
                        <td>{fmtDate(t.added_at)}</td>
                        <td>{t.added_by || '—'}</td>
                        <td>
                          <span className={`bdp-badge ${t.removed_at ? 'bdp-badge-removed' : 'bdp-badge-active'}`}>
                            {t.removed_at ? 'Removed' : 'Active'}
                          </span>
                        </td>
                        <td>{t.removed_reason || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>

        {/* ====== SECTION 3: Weight & Invoicing ====== */}
        <div className="bdp-card">
          <div className="bdp-card-header" onClick={() => tog('invoice', !s3, setS3)}>
            <span className="bdp-card-title">Weight &amp; Invoicing</span>
            <Chevron open={!s3} />
          </div>
          {!s3 && (
            <div className="bdp-card-body">
              {!isClosed ? (
                <>
                  <div className="bdp-invoice-gate">
                    Weight and invoicing will be available once this BOL is closed by the operator.
                  </div>
                  <div className="bdp-field" style={{ marginTop: 'var(--space-4)' }}>
                    <span className="bdp-field-label">Estimated Weight</span>
                    <span className="bdp-field-value">
                      {bol.estimated_weight_lbs != null ? `${Number(bol.estimated_weight_lbs).toLocaleString()} lbs` : '—'}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <div className="bdp-grid">
                    <div className="bdp-field">
                      <span className="bdp-field-label">Estimated Weight at Close</span>
                      <span className="bdp-field-value">
                        {bol.estimated_weight_lbs != null ? `${Number(bol.estimated_weight_lbs).toLocaleString()} lbs` : '—'}
                      </span>
                    </div>
                    <div className="bdp-field">
                      <span className="bdp-field-label">Actual Weight (lbs)</span>
                      <input className="bdp-input" type="number" placeholder="Enter weight from ticket"
                        value={weight} onChange={e => { setWeight(e.target.value); setDirty(true); }} />
                    </div>
                  </div>
                  {invoiceAmt != null && (
                    <div className="bdp-derived">
                      Invoice: ${invoiceAmt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  )}
                  <div className="bdp-invoice-row">
                    <label className="bdp-invoice-check">
                      <input type="checkbox" checked={weightTicketReceived}
                        onChange={e => { setWeightTicketReceived(e.target.checked); setDirty(true); }} />
                      Weight Ticket Received
                    </label>
                  </div>
                  <div className="bdp-invoice-row">
                    <label className="bdp-invoice-check">
                      <input type="checkbox" checked={invoiced}
                        disabled={!wNum || wNum <= 0}
                        onChange={e => { setInvoiced(e.target.checked); setDirty(true); }} />
                      Mark as Invoiced
                    </label>
                    {(!wNum || wNum <= 0) && (
                      <span style={{ fontSize: 'var(--text-2xs)', color: 'var(--color-text-muted)' }}>
                        Enter actual weight first
                      </span>
                    )}
                  </div>
                  {invoiced && bol.invoiced_at && (
                    <div style={{ fontSize: 'var(--text-2xs)', color: 'var(--color-text-muted)', marginTop: 'var(--space-3)' }}>
                      Invoiced {fmtDatetime(bol.invoiced_at)} by {bol.invoiced_by || '—'}
                    </div>
                  )}
                  {dirty && (
                    <div style={{ marginTop: 'var(--space-5)' }}>
                      <button className="bdp-btn bdp-btn-primary" onClick={handleSaveInvoice} disabled={saving}>
                        {saving ? 'Saving…' : 'Save'}
                      </button>
                    </div>
                  )}
                  {saveError && <div className="bdp-error">{saveError}</div>}
                </>
              )}
            </div>
          )}
        </div>

        {/* ====== SECTION 4: Documents ====== */}
        <div className="bdp-card">
          <div className="bdp-card-header" onClick={() => tog('docs', !s4, setS4)}>
            <span className="bdp-card-title">Documents</span>
            <div className="bdp-card-actions" onClick={e => e.stopPropagation()}>
              <button className="bdp-btn bdp-btn-primary" onClick={() => setShowDocsModal(true)}>+ Add Document</button>
            </div>
            <Chevron open={!s4} />
          </div>
          {!s4 && (
            <div className="bdp-card-body">
              {docs.length === 0 ? (
                <div className="bdp-empty">No documents attached. Use Add Document to attach weight tickets or BOL scans.</div>
              ) : (
                <div className="bdp-doc-list">
                  {docs.map((d, i) => (
                    <div key={d.blob_name || i} className="bdp-doc-item">
                      <span className="bdp-doc-type">{d.document_type || 'OTHER'}</span>
                      <button className="bdp-doc-link" onClick={() => handleDocDownload(d.blob_name)}>
                        {d.original_filename || d.blob_name}
                      </button>
                      <span className="bdp-doc-meta">
                        {d.uploaded_by && `${d.uploaded_by}`}
                        {d.uploaded_at && ` · ${fmtDate(d.uploaded_at)}`}
                        {d.file_size_bytes && ` · ${fmtSize(d.file_size_bytes)}`}
                      </span>
                      <button className="bdp-doc-delete" onClick={() => handleDocDelete(d.blob_name)}>Delete</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ====== SECTION 5: Photos ====== */}
        <div className="bdp-card">
          <div className="bdp-card-header" onClick={() => tog('photos', !s5, setS5)}>
            <span className="bdp-card-title">Photos</span>
            <Chevron open={!s5} />
          </div>
          {!s5 && (
            <div className="bdp-card-body">
              {photos.length === 0 ? (
                <div className="bdp-empty">No photos captured for this shipment.</div>
              ) : (
                <div className="bdp-photo-grid">
                  {photos.map((p, i) => (
                    <button key={p.blob_name || i} className="bdp-photo-thumb"
                      onClick={() => {
                        window.history.pushState({ lightbox: true }, '');
                        setLightboxUrl(p.blob_name);
                      }} title={p.caption || p.original_filename || ''}>
                      <img src={p.blob_name} alt="" className="bdp-photo-img" loading="lazy" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ====== SECTION 6: Signature (conditional) ====== */}
        {hasSig && (
          <div className="bdp-card">
            <div className="bdp-card-header" onClick={() => tog('sig', !s6, setS6)}>
              <span className="bdp-card-title">Signature</span>
              <Chevron open={!s6} />
            </div>
            {!s6 && (
              <div className="bdp-card-body">
                <div className="bdp-sig-card">
                  <img src={bol.signature_image_url} alt="Driver signature" className="bdp-sig-img" />
                </div>
                {bol.driver_name_printed && (
                  <div className="bdp-sig-label">Signed by: {bol.driver_name_printed}</div>
                )}
                {bol.closed_at && (
                  <div className="bdp-sig-label">Signed at: {fmtDatetime(bol.closed_at)}</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Docs modal */}
      {showDocsModal && (
        <BolDocumentsModal
          bolId={bolId}
          projectId={projectId}
          existingDocuments={docs}
          onSave={() => { loadBol(); setShowDocsModal(false); }}
          onClose={() => setShowDocsModal(false)}
        />
      )}

      {/* Lightbox */}
      {lightboxUrl && (
        <div className="bdp-lightbox" onClick={closeLightbox}>
          <img src={lightboxUrl} alt="" onClick={e => e.stopPropagation()} />
        </div>
      )}

      {/* Toast */}
      {toast && <div className="bdp-toast">{toast}</div>}
    </div>
  );
}
